import { Dispatch, FC, SetStateAction } from 'react';
import classes from './styles.module.scss';
import { IInfrastructureProject } from '../../../types/project.types';
import { MinusIcon, PlusBigIcon } from '../../../assets/icons';
import { useTranslation } from 'react-i18next';
import { convertFilesToImageDate, toTranslationKey } from '../../../helpers/utils';
import Documentation from '../components/documentation.component';
import { formatDate } from '../../home/components/left-menu/info.component';
import ImageGrid from '../components/image-grid.component';
import AdditionalFields from '../components/additional-fields.component';

interface IProps {
  list: IInfrastructureProject[];
  isOpen: Record<string, boolean>;
  setIsOpen: Dispatch<SetStateAction<Record<string, boolean>>>;
}

const translationKey = 'projects.infrastructure-projects.education';
const baseTranslationKey = 'projects.infrastructure-projects';

const EducationTab: FC<IProps> = ({ list, isOpen, setIsOpen }) => {
  const { t, i18n: { language } } = useTranslation();
  const suffix = language === 'ru' ? 'Ru' : 'Kz';

  const renderFieldRowItem = (labelKey: string, value: string | number | undefined, unit?: string) => (
    <div className={classes['field-row__item']}>
      <span className={classes['field-row__label']}>{t(labelKey)}:</span>
      <span className={classes['field-row__value']}>
        {value} {unit || ''}
      </span>
    </div>
  );

  const renderSportSections = (item: IInfrastructureProject) => {
    if (!item.sportSections || Object.keys(item.sportSections).length === 0) return null;
    return (
      <div className={classes['field-block']}>
        <div className={classes['field-block__title']}>
          {t(`${baseTranslationKey}.sport-sections.title`)}
        </div>
        <div className={classes['field-row']}>
          {Object.entries(item.sportSections).map(([key, value]) => (
            <div key={key} className={classes['field-row__item']}>
              <span className={classes['field-row__label']}>
                {t(`${baseTranslationKey}.sport-sections.${key}`)}
              </span>
              {key !== 'no' && (
                <span className={classes['field-row__value']}>{value}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderRequirementsBlock = (item: IInfrastructureProject) => {
    if (!item.requirements) return null;

    const isNeeded = item.requirements !== 'NOT_NEEDED';
    const label = t(`${baseTranslationKey}.${toTranslationKey(item.requirements)}`);
    const description = item[`requirements${suffix}`];

    return (
      <>
        <div className={classes['field-block']}>
          <div className={classes['field-block__title']}>
            {t(`${baseTranslationKey}.renovation`)}
          </div>
          <div className={classes['field-row']}>
            <div className={classes['field-row__item']}>
              {isNeeded && (
                <span className={classes['field-row__label']}>
                  {t(`${baseTranslationKey}.needed`)}:
                </span>
              )}
              <span className={classes['field-row__value']}>
                {label}
                {isNeeded && description && `, ${description}`}
              </span>
            </div>
            {isNeeded && item.lastMajorRenovation && (
              <div className={classes['field-row__item']}>
                <span className={classes['field-row__label']}>
                  {t(`${baseTranslationKey}.last-major-renovation`)}
                </span>
                <span className={classes['field-row__value']}>
                  {formatDate(item.lastMajorRenovation)}
                </span>
              </div>
            )}
          </div>
        </div>
        <Documentation data={item} />
      </>

    );
  };

  return (
    <div className={classes['items-list']}>
      {list.map(item => (
        <div key={item.id} className={classes['project-item']}>
          <div
            className={classes['project-item__header']}
            onClick={() =>
              setIsOpen(prev => ({ ...prev, [`EDUCATION-${item.id}`]: !prev[`EDUCATION-${item.id}`] }))
            }
          >
            <div className={classes['project-item__icon']}>
              {isOpen[`EDUCATION-${item.id}`] ? <MinusIcon /> : <PlusBigIcon />}
            </div>
            <span className={classes['project-item__title']}>
              {item[`name${suffix}`] ?? '-'}
            </span>

            {item.constructionYear && <div className={classes['project-item__year']}>
              <span>{t(`${translationKey}.constructionYear`)}: </span>
              <span>{item.constructionYear ?? ''}</span>
            </div>}
          </div>

          {isOpen[`EDUCATION-${item.id}`] && (
            <div className={classes['project-item__body']}>
              <div className={classes['field-row']}>
                {['constructionYear', 'factInfo', 'power'].map(field => {
                  const value = item[field as keyof IInfrastructureProject] as string | number | undefined;
                  const unit =
                    field === 'constructionYear'
                      ? ''
                      : item.isSchool
                        ? t(`${translationKey}.students`)
                        : t(`${translationKey}.children`);
                  return renderFieldRowItem(`${translationKey}.${field}`, value, unit);
                })}

                {renderFieldRowItem(
                  `${translationKey}.factCondition`,
                  t(`${baseTranslationKey}.${item.factCondition?.toLowerCase()}`)
                )}
              </div>

              {renderSportSections(item)}
              {renderRequirementsBlock(item)}
              {item.additionalFields && <AdditionalFields data={item.additionalFields} />}
              {item.files && <ImageGrid images={convertFilesToImageDate(item.files)} />}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default EducationTab;
